// @owners { team: patients-team }
import { Body, Button, Card, Icon, ListItem, MdSpacing, SecondaryPage } from '@alto/design-system';
import { useQueryInsuranceConfigs } from '@alto/features';
import { useNavigation } from '@alto/navigation';
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { routeFromPath } from '~shared/features/onboarding/helpers/routeToPath';
import { useAnalytics } from '~shared/hooks/useAnalytics';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useGetInsurances } from '~shared/queries/query-keys/insurances';
import { useSelectorShared } from '~shared/store';
import { OnboardingNavBarLogo } from './OnboardingNavBarLogo';
import { OnboardingHeader } from '~web/features/onboarding/components/redesign/OnboardingHeader';
import { previousUrl as getPreviousUrl } from '~web/features/onboarding/helpers/urlHandler';
import {
  getOnboardingInsuranceInputSubtitle,
  getOnboardingInsuranceInputTitle,
} from '~web/features/onboarding/helpers/utils';

export type Props = {
  readonly onUploadPhoto: () => void;
  readonly onEnterManually: () => void;
  readonly onDeclineInsurance: () => Promise<void>;
  readonly onSkip: () => void;
  readonly showProgyny: boolean;
  readonly origin: string;
};

export const OnboardingInsuranceInput = ({
  onDeclineInsurance,
  onEnterManually,
  onSkip,
  onUploadPhoto,
  origin,
  showProgyny,
}: Props) => {
  const { trackPageView } = useAnalytics();
  const { navigate } = useNavigation();
  const location = useLocation();
  const currentRoute = routeFromPath(location.pathname);
  const { hasInsurance } = useGetInsurances();
  const previousUrl = getPreviousUrl({ params: { hasInsurance }, currentRoute });
  const declineInsuranceLoading = useSelectorShared((state) => state.ui.loading.declineInsuranceLoading);
  const { headerBackgroundColor, headerLogoUrl } = useQueryInsuranceConfigs();

  const titleText = getOnboardingInsuranceInputTitle({
    hasInsurance,
    showProgyny,
  });
  const subtitleText = getOnboardingInsuranceInputSubtitle({
    showProgyny,
    hasInsurance,
  });

  const onBack = () => {
    if (previousUrl) {
      navigate(previousUrl);
    }
  };

  useEffect(() => {
    trackPageView({
      event: EVENTS.ONBOARDING__INSURANCE_INPUT_VIEWED,
      params: { origin, is_progyny_rx_view: showProgyny },
    });
  }, [origin, showProgyny, trackPageView]);

  const handleSelectInsuranceInputMethod = (insuranceType: 'PHOTO' | 'MANUAL' | 'NONE') => {
    switch (insuranceType) {
      case 'PHOTO':
        return onUploadPhoto;

      case 'MANUAL':
        return onEnterManually;

      case 'NONE':
        return onDeclineInsurance;

      default:
        return;
    }
  };

  return (
    <SecondaryPage
      headerBackgroundColor={headerBackgroundColor}
      withoutWebFramingElementHeights
      dismissIcon="chevronleft"
      onDismiss={onBack}
      NavBarCenterContent={<OnboardingNavBarLogo illustrationSrc={headerLogoUrl} />}
      HeaderContent={
        <OnboardingHeader
          title={titleText}
          subtitle={<Body center>{subtitleText}</Body>}
        />
      }
      buttons={[
        <Button
          key="skip"
          label={hasInsurance ? 'Cancel' : 'Remind me later'}
          onPress={onSkip}
          type="tertiary"
        />,
      ]}
      footerPlacementContext="webScreenWithoutPolicyFooter"
    >
      <Card>
        <ListItem
          title="Take a photo"
          LeftContent={<Icon name="camera" />}
          RightContent={
            <Icon
              name="chevronright-small"
              type="grey"
            />
          }
          onPress={handleSelectInsuranceInputMethod('PHOTO')}
        />
      </Card>
      <MdSpacing />
      <Card>
        <ListItem
          title="Enter information manually"
          LeftContent={<Icon name="signpaper" />}
          RightContent={
            <Icon
              name="chevronright-small"
              type="grey"
            />
          }
          onPress={handleSelectInsuranceInputMethod('MANUAL')}
        />
      </Card>
      {hasInsurance ? null : (
        <>
          <MdSpacing />
          <Card>
            <ListItem
              title="I don't have any benefits"
              LeftContent={<Icon name="shieldslash" />}
              RightContent={
                <Icon
                  name="chevronright-small"
                  type="grey"
                />
              }
              onPress={handleSelectInsuranceInputMethod('NONE')}
              loading={declineInsuranceLoading}
            />
          </Card>
        </>
      )}
    </SecondaryPage>
  );
};
